import { useEffect, useRef } from "react";
import closeIcon from "../../Assets/icon/explore/close.svg";
import { ArchiveGlyph, PauseGlyph } from "./lifecycleIcons";
import { LIFECYCLE_COPY } from "./copy";
import "./lifecycle.css";

/**
 * The Pause / Archive confirmation (Figma 11115:27727 / 11115:27892): one
 * bottom sheet, `kind="pause" | "archive"`. A phone gets a bottom sheet, a
 * wider screen a centred card — the same mechanics as ModalitySheet.
 *
 * Accessible as a modal dialog: focus moves in on open and is trapped
 * (Tab / Shift+Tab cycle inside), Esc closes, the page behind stops
 * scrolling, and focus returns to the trigger on close.
 *
 * `busy` (the move is in flight) disables every control, Esc and the
 * backdrop, so one confirm is one call.
 */
const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

function LifecycleSheet({ open, kind, busy, onConfirm, onClose }) {
  const panelRef = useRef(null);
  const confirmRef = useRef(null);
  const copy = LIFECYCLE_COPY.sheet[kind];

  // Focus in on open, back to the trigger on close; lock the page's scroll.
  useEffect(() => {
    if (!open) return undefined;
    const trigger = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    confirmRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (trigger && typeof trigger.focus === "function") trigger.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (!busy) onClose?.();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
      if (nodes.length === 0) {
        e.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const inside = panelRef.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open || !copy) return null;
  const titleId = `exl-sheet-title-${kind}`;

  return (
    <div className="ex-sheet exl-sheet" role="presentation" onClick={busy ? undefined : onClose}>
      <div
        ref={panelRef}
        className={`ex-sheet__panel exl-sheet__panel exl-sheet__panel--${kind}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy ? "true" : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="exl-sheet__close"
          aria-label={LIFECYCLE_COPY.sheet.close}
          disabled={busy}
          onClick={onClose}
        >
          <img src={closeIcon} alt="" width="20" height="20" />
        </button>
        <span className={`exl-sheet__glow exl-sheet__glow--${kind}`} aria-hidden="true">
          <span className="exl-sheet__icon">
            {kind === "pause" ? <PauseGlyph size={36} /> : <ArchiveGlyph size={36} />}
          </span>
        </span>
        <div className="exl-sheet__text">
          <h2 id={titleId} className="exl-sheet__title">
            {copy.title}
          </h2>
          {copy.body.map((line) => (
            <p key={line} className="exl-sheet__body">
              {line}
            </p>
          ))}
        </div>
        <div className="exl-sheet__buttons">
          <button
            type="button"
            className="exl-btn exl-btn--outline"
            disabled={busy}
            onClick={onClose}
          >
            {LIFECYCLE_COPY.sheet.cancel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="exl-btn exl-btn--primary"
            disabled={busy}
            onClick={() => onConfirm?.(kind)}
          >
            {copy.confirm}
          </button>
        </div>
      </div>
    </div>
  );
}

export default LifecycleSheet;
